import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import type {
    NewRelicBrowserMonitoringConfig,
    RequiredNewRelicBrowserMonitoringConfig,
} from '../../common/config/configTypes';
import getClientConfig from '../utils/getClientConfig';
import { buildNewRelicBrowserAgentOptions } from './buildNewRelicBrowserAgentOptions';
import { getNewRelicBrowserAgent, setNewRelicBrowserAgent } from './newRelicAgentState';
import { setNewRelicErrorHandler } from './newRelicUtils';

/** Check whether browser monitoring has the fields required to bootstrap the Browser agent. */
const hasRequiredBrowserMonitoringConfig = (
    browserMonitoring?: NewRelicBrowserMonitoringConfig,
): browserMonitoring is RequiredNewRelicBrowserMonitoringConfig =>
    !!(
        browserMonitoring?.enabled &&
        browserMonitoring.accountId &&
        browserMonitoring.applicationId &&
        browserMonitoring.licenseKey
    );

/** Boot the Browser agent from the shared client config, the same way on Web, Electron and Capacitor. */
export const initNewRelicBrowserAgent = () => {
    if (getNewRelicBrowserAgent()) return; // already booted -- calling again would construct a second, competing agent

    const clientConfig = getClientConfig();
    const browserMonitoring = clientConfig.newrelic?.browserMonitoring;
    if (!hasRequiredBrowserMonitoringConfig(browserMonitoring)) return;
    if (!clientConfig.urlBase || !clientConfig.collaboration?.url || !clientConfig.mediaServer?.apiUrl) return;

    try {
        const options = buildNewRelicBrowserAgentOptions(
            browserMonitoring,
            {
                urlBase: clientConfig.urlBase,
                collaboration: clientConfig.collaboration,
                mediaServer: clientConfig.mediaServer,
            },
            { enabled: clientConfig.newrelic?.distributedTracingEnabled ?? false },
        );

        setNewRelicBrowserAgent(new BrowserAgent(options));

        // No init-time config for this - setErrorHandler is runtime-API-only, so register it here,
        // right after boot, rather than leaving it for the analytics middleware to reach later.
        setNewRelicErrorHandler();
    } catch (err) {
        console.error('Failed to initialise New Relic Browser agent', err); // logger isn't set up yet
    }
};

// Side effect of import, not a call site — see commit message for why (Babel import hoisting).
initNewRelicBrowserAgent();
